import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { allPosts, type BlogSection } from "../../../mocks/newsData";
import Navbar from "../../home/components/Navbar";
import Footer from "../../home/components/Footer";
import { useState } from "react";
import QuoteModal from "../../home/components/QuoteModal";

const categoryColours: Record<string, string> = {
  Environment: "bg-emerald-100 text-emerald-800",
  "Tips & Guides": "bg-amber-100 text-amber-800",
  "Company News": "bg-sky-100 text-sky-800",
  Industry: "bg-rose-100 text-rose-800",
};

function renderSection(section: BlogSection, idx: number) {
  switch (section.type) {
    case "intro":
      return (
        <p key={idx} className="text-lg md:text-xl text-cl-dark/80 leading-relaxed font-semibold border-l-4 border-cl-green pl-5 py-1 mb-8">
          {section.content}
        </p>
      );
    case "h2":
      return (
        <h2 key={idx} className="text-2xl md:text-3xl font-black text-cl-dark mt-12 mb-4 leading-tight">
          {section.content}
        </h2>
      );
    case "h3":
      return (
        <h3 key={idx} className="text-xl font-black text-cl-dark mt-8 mb-3">
          {section.content}
        </h3>
      );
    case "p":
      return (
        <p key={idx} className="text-base md:text-lg text-cl-dark/75 leading-relaxed font-medium mb-5">
          {section.content}
        </p>
      );
    case "ul":
      return (
        <ul key={idx} className="mb-6 flex flex-col gap-2.5">
          {section.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-base text-cl-dark/75 font-medium leading-relaxed">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-cl-green flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={idx} className="mb-6 flex flex-col gap-3">
          {section.items?.map((item, i) => (
            <li key={i} className="flex items-start gap-4 text-base text-cl-dark/75 font-medium leading-relaxed">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-cl-dark text-white font-black text-xs flex items-center justify-center">
                {i + 1}
              </span>
              <span className="pt-0.5">{item}</span>
            </li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <div key={idx} className="my-8 bg-cl-tint border-2 border-cl-green/30 rounded-2xl p-6 flex gap-4 items-start">
          <span className="w-10 h-10 rounded-xl bg-cl-green/10 flex items-center justify-center flex-shrink-0">
            <i className={`${section.icon || "ri-information-line"} text-cl-green text-lg`} />
          </span>
          <p className="text-base text-cl-dark font-semibold leading-relaxed">{section.content}</p>
        </div>
      );
    case "cta":
      return (
        <div key={idx} className="my-10 bg-cl-dark rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white font-black text-lg">{section.content}</p>
          <Link
            to="/book"
            className="bg-cl-green text-white font-black px-8 py-3.5 rounded-2xl whitespace-nowrap cartoon-btn-green flex items-center gap-2 flex-shrink-0"
          >
            {section.label || "Book Now"} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      );
    default:
      return null;
  }
}

export default function NewsPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [quoteOpen, setQuoteOpen] = useState(false);

  const post = allPosts.find((p) => p.slug === slug);
  const related = allPosts.filter((p) => p.slug !== slug && p.category === post?.category).slice(0, 3);
  const otherRelated = related.length < 3
    ? [...related, ...allPosts.filter((p) => p.slug !== slug && p.category !== post?.category)].slice(0, 3)
    : related;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar onBookNow={() => setQuoteOpen(true)} />
        <main className="flex-1 flex items-center justify-center bg-cl-tint">
          <div className="text-center py-32 px-6">
            <i className="ri-newspaper-line text-5xl text-cl-green mb-4 block" />
            <h1 className="text-3xl font-black text-cl-dark mb-3">Article Not Found</h1>
            <p className="text-cl-dark/60 font-semibold mb-8">This article doesn't exist or may have been moved.</p>
            <Link to="/news" className="bg-cl-dark text-white font-black px-8 py-3.5 rounded-2xl cartoon-btn">
              Back to News
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catColour = categoryColours[post.category] ?? "bg-gray-100 text-gray-700";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar onBookNow={() => setQuoteOpen(true)} />

      {/* Hero */}
      <header className="bg-cl-dark pt-28 pb-0 md:pt-36">
        <div className="max-w-screen-lg mx-auto px-6 lg:px-10 pb-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/40 text-sm font-semibold mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/news" className="hover:text-white transition-colors">News</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-white/60 truncate max-w-[200px]">{post.title}</span>
          </div>

          {/* Category + meta */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className={`text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full border-2 border-cl-dark/20 ${catColour}`}>
              {post.category}
            </span>
            <span className="text-white/40 text-sm font-semibold">{post.date}</span>
            <span className="text-white/40 text-sm">·</span>
            <span className="text-white/40 text-sm font-semibold flex items-center gap-1">
              <i className="ri-time-line text-xs" /> {post.readTime}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-6 max-w-3xl">
            {post.title}
          </h1>

          {/* Author */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 flex-shrink-0">
              <img src={post.author.avatar} alt={post.author.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-white font-black text-sm">{post.author.name}</p>
              <p className="text-white/50 text-xs font-semibold">{post.author.role}</p>
            </div>
          </div>
        </div>

        {/* Hero image — bleeds out of dark header */}
        <div className="max-w-screen-lg mx-auto px-6 lg:px-10">
          <div className="rounded-t-3xl overflow-hidden" style={{ height: "420px" }}>
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </header>

      {/* Article body */}
      <main className="flex-1">
        <div className="max-w-screen-lg mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-12 pt-12 pb-20">
            {/* Content */}
            <article className="min-w-0">
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.map((tag) => (
                  <span key={tag} className="text-xs font-semibold bg-cl-tint text-cl-green px-3 py-1 rounded-full border border-cl-green/20">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Body sections */}
              <div>
                {post.body.map((section, idx) => renderSection(section, idx))}
              </div>

              {/* Share bar */}
              <div className="border-t-2 border-gray-100 pt-8 mt-10 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 flex-shrink-0">
                    <img src={post.author.avatar} alt={post.author.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-black text-cl-dark">Written by {post.author.name}</p>
                    <p className="text-xs text-cl-dark/50 font-semibold">{post.author.role} · {post.date}</p>
                  </div>
                </div>
                <Link
                  to="/news"
                  className="flex items-center gap-2 text-sm font-black text-cl-dark hover:text-cl-green transition-colors"
                >
                  <i className="ri-arrow-left-line" /> All articles
                </Link>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 flex flex-col gap-5">
                {/* Book CTA */}
                <div className="bg-cl-dark rounded-2xl p-6 cartoon-card">
                  <p className="text-cl-lime text-xs font-black uppercase tracking-widest mb-2">Ready to clear?</p>
                  <p className="text-white font-black text-lg leading-snug mb-4">Same-day rubbish removal from £99</p>
                  <Link
                    to="/book"
                    className="block w-full text-center bg-cl-green text-white font-black py-3.5 rounded-xl cartoon-btn-green text-sm"
                  >
                    Book Now →
                  </Link>
                </div>

                {/* Related posts */}
                {otherRelated.length > 0 && (
                  <div>
                    <p className="text-xs font-black text-cl-dark/40 uppercase tracking-widest mb-3">More articles</p>
                    <div className="flex flex-col gap-3">
                      {otherRelated.map((rel) => (
                        <Link key={rel.id} to={`/news/${rel.slug}`} className="group flex gap-3 items-start">
                          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 border-transparent group-hover:border-cl-green transition-colors">
                            <img src={rel.image} alt={rel.title} className="w-full h-full object-cover object-top" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-black text-cl-dark leading-snug group-hover:text-cl-green transition-colors line-clamp-2">{rel.title}</p>
                            <p className="text-xs text-cl-dark/40 font-semibold mt-1">{rel.readTime}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>

        {/* More articles strip */}
        {otherRelated.length > 0 && (
          <section className="bg-cl-tint border-t-2 border-cl-dark/10 py-16">
            <div className="max-w-screen-lg mx-auto px-6 lg:px-10">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black text-cl-dark">More to read</h2>
                <Link to="/news" className="text-sm font-black text-cl-green flex items-center gap-1 hover:underline">
                  All articles <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherRelated.map((rel) => (
                  <Link key={rel.id} to={`/news/${rel.slug}`} className="group block">
                    <article className="bg-white rounded-2xl overflow-hidden cartoon-card h-full flex flex-col transition-transform duration-300 group-hover:-translate-y-1">
                      <div className="relative overflow-hidden" style={{ height: "180px" }}>
                        <img src={rel.image} alt={rel.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                        <span className={`absolute top-3 left-3 text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full border-2 border-cl-dark/10 ${categoryColours[rel.category] ?? "bg-gray-100 text-gray-700"}`}>
                          {rel.category}
                        </span>
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <p className="text-cl-green text-xs font-black uppercase tracking-widest mb-2">{rel.date} · {rel.readTime}</p>
                        <h3 className="text-cl-dark font-black text-sm leading-snug group-hover:text-cl-green transition-colors flex-1">{rel.title}</h3>
                        <div className="flex items-center gap-2 mt-4 border-t border-gray-100 pt-3">
                          <div className="w-6 h-6 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
                            <img src={rel.author.avatar} alt={rel.author.name} className="w-full h-full object-cover" />
                          </div>
                          <span className="text-xs font-black text-cl-dark/50">{rel.author.name}</span>
                          <span className="ml-auto text-cl-green font-black text-xs">Read →</span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      {quoteOpen && <QuoteModal isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}
